import Logger from '../Logger/Logger';
import PubSub from './pubsub-js';
import {
  EventDataInterface,
  Transformer,
  EventInterface,
  Listener,
  SubscriptionId,
  SubscriptionRequest,
} from './types/eventTypes';

const EventService = (function (): EventInterface {
  const transformerInstances = new Map<string, Transformer>();
  const loggerServiceInstance = Logger.getInstance();
  return {
    subscribe: function (topic: string, version: string, listener: Listener) {
      const eventServiceListener = (message: string, data: EventDataInterface) => {
        let transformedPayload: EventDataInterface;
        if (transformerInstances.has(topic)) {
          transformedPayload = transformerInstances.get(topic)?.(version, data) as EventDataInterface;
        } else {
          transformedPayload = data;
        }
        listener(message, transformedPayload);
      };
      const subscribed: string = PubSub.subscribe(topic, eventServiceListener);
      loggerServiceInstance.debug(`Event Service: ${topic} subscribed with version ${version}.`);
      return subscribed;
    },
    /**
     *
     * @param subscriptionRequests
     * @param listener
     * @returns
     */
    subscribeMultiple: function (
      subscriptionRequests: SubscriptionRequest | SubscriptionRequest[],
      listener: Listener,
    ) {
      const topicsData: SubscriptionRequest[] = Array.isArray(subscriptionRequests)
        ? subscriptionRequests
        : Array.of(subscriptionRequests);
      return topicsData.map((subscription: SubscriptionRequest) => {
        const eventServiceListener = (message: string, data: EventDataInterface) => {
          let payload: EventDataInterface;
          if (transformerInstances.has(subscription.topic)) {
            payload = transformerInstances.get(subscription.topic)?.(subscription.version, data) as EventDataInterface;
          } else {
            payload = data;
          }
          listener(message, payload);
        };
        const subscribed: string = PubSub.subscribe(subscription.topic, eventServiceListener);
        loggerServiceInstance.debug(
          `Event Service: ${subscription.topic} subscribed with version ${subscription.version}.`,
        );
        return subscribed;
      });
    },
    publish: function (topic: string, eventData: EventDataInterface, transformer?: Transformer) {
      if (transformer) {
        transformerInstances.set(topic, transformer);
      }
      const published: boolean = PubSub.publish(topic, eventData);
      loggerServiceInstance.debug(`Event Service: ${topic} has been published in asynchronous manner.`);
      return published;
    },
    publishSync: function (topic: string, eventData: EventDataInterface, transformer?: Transformer) {
      if (transformer) {
        transformerInstances.set(topic, transformer);
      }
      const published: boolean = PubSub.publishSync(topic, eventData);
      loggerServiceInstance.debug(`Event Service: ${topic} has been published in sychronous manner.`);
      return published;
    },
    unsubscribe: function (topic: string | Listener) {
      return PubSub.unsubscribe(topic);
    },
    unsubscribeMultiple: function (subscriptionIds: SubscriptionId) {
      const topicsData: string[] = Array.isArray(subscriptionIds) ? subscriptionIds : Array.of(subscriptionIds);
      return topicsData.map((sub) => EventService.unsubscribe(sub));
    },
    countSubscriptions: function (token: string) {
      return PubSub.countSubscriptions(token);
    },
    clearAllSubscriptions: function () {
      PubSub.clearAllSubscriptions();
      loggerServiceInstance.debug('Event Service: Cleared all subscriptions.');
    },
    getSubscriptions: function (token: string) {
      return PubSub.getSubscriptions(token);
    },
  };
})();

export default EventService;
